import { MapStyle, RoomShape, RoomShapeFamily } from '../../types/map.types';
import { emptyMask, keepLargestComponent, Mask } from './mask';
import { STYLE_PRESETS, StyleParams } from './style-presets';

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function shape(mask: Mask): RoomShape {
  return { mask, width: mask[0]?.length ?? 0, height: mask.length };
}

export function makeRect(w: number, h: number): RoomShape {
  const mask = emptyMask(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      mask[y][x] = true;
    }
  }
  return shape(mask);
}

function makeEllipse(w: number, h: number): RoomShape {
  const mask = emptyMask(w, h);
  const cx = w / 2;
  const cy = h / 2;
  const rx = w / 2;
  const ry = h / 2;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x + 0.5 - cx) / rx;
      const dy = (y + 0.5 - cy) / ry;
      if (dx * dx + dy * dy <= 1) {
        mask[y][x] = true;
      }
    }
  }
  return shape(keepLargestComponent(mask));
}

function makeOctagon(w: number, h: number): RoomShape {
  const mask = emptyMask(w, h);
  const cut = Math.max(1, Math.floor(Math.min(w, h) / 3));
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const fromLeft = x;
      const fromRight = w - 1 - x;
      const fromTop = y;
      const fromBottom = h - 1 - y;
      const cornerTL = fromLeft + fromTop < cut;
      const cornerTR = fromRight + fromTop < cut;
      const cornerBL = fromLeft + fromBottom < cut;
      const cornerBR = fromRight + fromBottom < cut;
      mask[y][x] = !(cornerTL || cornerTR || cornerBL || cornerBR);
    }
  }
  return shape(mask);
}

function makeLShape(w: number, h: number): RoomShape {
  const mask = emptyMask(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      mask[y][x] = true;
    }
  }
  const cutW = randInt(Math.max(1, Math.floor(w / 3)), Math.floor(w / 2));
  const cutH = randInt(Math.max(1, Math.floor(h / 3)), Math.floor(h / 2));
  const corner = randInt(0, 3);
  for (let y = 0; y < cutH; y++) {
    for (let x = 0; x < cutW; x++) {
      const tx = corner === 1 || corner === 3 ? w - 1 - x : x;
      const ty = corner === 2 || corner === 3 ? h - 1 - y : y;
      mask[ty][tx] = false;
    }
  }
  return shape(keepLargestComponent(mask));
}

function makeDiamond(w: number, h: number): RoomShape {
  const mask = emptyMask(w, h);
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const rx = (w - 1) / 2;
  const ry = (h - 1) / 2;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (Math.abs(x - cx) / rx + Math.abs(y - cy) / ry <= 1.001) {
        mask[y][x] = true;
      }
    }
  }
  return shape(keepLargestComponent(mask));
}

function makeCross(w: number, h: number): RoomShape {
  const mask = emptyMask(w, h);
  const armW = Math.max(1, Math.floor(w / 3));
  const armH = Math.max(1, Math.floor(h / 3));
  const xLo = Math.floor((w - armW) / 2);
  const xHi = xLo + armW - 1;
  const yLo = Math.floor((h - armH) / 2);
  const yHi = yLo + armH - 1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const vertical = x >= xLo && x <= xHi;
      const horizontal = y >= yLo && y <= yHi;
      mask[y][x] = vertical || horizontal;
    }
  }
  return shape(mask);
}

function makeComposite(w: number, h: number): RoomShape {
  const mask = emptyMask(w, h);
  const blocks = randInt(2, 3);
  for (let i = 0; i < blocks; i++) {
    const bw = randInt(Math.max(2, Math.floor(w / 2)), w);
    const bh = randInt(Math.max(2, Math.floor(h / 2)), h);
    const ox = randInt(0, w - bw);
    const oy = randInt(0, h - bh);
    const useEllipse = Math.random() < 0.5;
    for (let y = 0; y < bh; y++) {
      for (let x = 0; x < bw; x++) {
        if (useEllipse) {
          const dx = (x + 0.5 - bw / 2) / (bw / 2);
          const dy = (y + 0.5 - bh / 2) / (bh / 2);
          if (dx * dx + dy * dy > 1) {
            continue;
          }
        }
        mask[oy + y][ox + x] = true;
      }
    }
  }
  return shape(keepLargestComponent(mask));
}

function makeOrganic(w: number, h: number, erosion: number): RoomShape {
  const base = makeEllipse(w, h).mask;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (base[y][x] && Math.random() < erosion) {
        base[y][x] = false;
      }
    }
  }
  const smoothed = emptyMask(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let n = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) {
            continue;
          }
          const nx = x + dx;
          const ny = y + dy;
          if (nx >= 0 && ny >= 0 && nx < w && ny < h && base[ny][nx]) {
            n++;
          }
        }
      }
      smoothed[y][x] = base[y][x] ? n >= 3 : n >= 5;
    }
  }
  return shape(keepLargestComponent(smoothed));
}

export function fromPattern(
  pattern: number[][],
  width: number,
  height: number,
): RoomShape {
  const mask = emptyMask(width, height);
  for (let y = 0; y < height; y++) {
    const row = pattern[y];
    for (let x = 0; x < width; x++) {
      mask[y][x] = (row?.[x] ?? 0) === 1;
    }
  }
  return shape(mask);
}

function generateFamily(
  family: RoomShapeFamily,
  w: number,
  h: number,
  p: StyleParams,
): RoomShape {
  switch (family) {
    case 'rect':
      return makeRect(w, h);
    case 'ellipse':
      return makeEllipse(w, h);
    case 'octagon':
      return makeOctagon(w, h);
    case 'lshape':
      return makeLShape(w, h);
    case 'cross':
      return makeCross(w, h);
    case 'diamond':
      return makeDiamond(w, h);
    case 'composite':
      return makeComposite(w, h);
    case 'organic':
      return makeOrganic(w, h, p.organicErosion ?? 0.3);
    case 'prefab':
      return makeRect(w, h);
  }
}

function pickFamily(p: StyleParams): RoomShapeFamily {
  const entries = Object.entries(p.shapeWeights) as [RoomShapeFamily, number][];
  if (entries.length === 0) {
    return 'rect';
  }
  const total = entries.reduce((s, [, wgt]) => s + wgt, 0);
  let roll = Math.random() * total;
  for (const [family, wgt] of entries) {
    roll -= wgt;
    if (roll <= 0) {
      return family;
    }
  }
  return entries[0][0];
}

export function sampleRoomShape(style: MapStyle): {
  shape: RoomShape;
  family: RoomShapeFamily;
} {
  const p = STYLE_PRESETS[style];
  const family = pickFamily(p);
  const w = randInt(p.minRoomSize, p.maxRoomSize);
  const h = randInt(p.minRoomSize, p.maxRoomSize);
  const result = generateFamily(family, w, h, p);
  if (result.mask.flat().some(Boolean)) {
    return { shape: result, family };
  }
  return { shape: makeRect(w, h), family: 'rect' };
}
